<i18n>
{
    "en": {},
    "ru": {
        "title": "{shelf_name} - Мой гардероб",
        "title_stranger": "{shelf_name} - Гардероб",
        "description": "Перемещайте полки между собой, удерживая палец на специальной иконке.",
        "description_desktop": "Перемещайте полки между собой, удерживая курсор на специальной иконке.",
        "share_shelf": "Поделиться полкой",
        "share_title": "{fullname} | {shelf_name} - Гардероб на Aromo.ru",
        "perfume_is_already_added": "Этот аромат уже добавлен на данную полку",
        "perfume_is_added_to_shelf": "Аромат добавлен на полку",
        "add_perfume": "Добавить аромат",
        "add_perfumes": "Добавить аромат",
        "change_perfumes": "Изменить ароматы",
        "set_shelf_main": "Сделать полку главной",
        "rename_shelf": "Переименовать полку",
        "hide_shelf": "Скрыть полку",
        "set_shelf_visible": "Сделать полку видимой",
        "remove_shelf": "Удалить полку",
        "remove_shelf_description": "Вы уверены, что хотите удалить полку <span class='bold'>“{shelf_name}”</span> со всеми ароматами? Это действие невозможно будет отменить.",
        "shelf_name_already_exist": "Полка с таким названием уже существует",
        "shelf_rename_error": "Изменение названия стандартной полки запрещено",
        "shelf_already_exists": "Полка с таким названием уже существует",
        "shelf_name_updated": "Полка переименована",
        "hide_shelf_text": "Ваша полка видна только вам",
        "set_shelf_visible_text": "Ваша полка видна всем пользователям"
    }
}
</i18n>

<template>
    <div class="wardrobe wardrobe_detail">
        <Breadcrumb
            showPrevButton
            prevPathName="users.wardrobe"
            class="wardrobe__breadcrumbs"
            :classModificator="'profile-pages'"
            :items="breadcrumbs" />

        <ProfileWrapper>
            <template #content>

                <div class="wardrobe__header-wrapper">
                    <h2 class="wardrobe__header">
                        {{ title }}
                        <span class="wardrobe__header-counter" v-if="total>0">{{ total }}</span>
                        <svg-icon
                            v-if="isMain"
                            class="shelf-icon"
                            name="icon-home"/>
                        <svg-icon
                            v-if="isHidden"
                            class="shelf-icon"
                            name="icon-crossed-eye"/>
                    </h2>

                    <div class="wardrobe__header-buttons">
                        <ButtonSquare
                            icon="icon-more-current"
                            class="wardrobe__button wardrobe__button-actions"
                            v-click-outside="hideActions"
                            @click.native="onMoreClick()"/>
                        <transition name="fade">
                            <Actions
                                v-show="showActions"
                                is-tooltip
                                :modalItems="actionModelItems"/>
                        </transition>
                    </div>
                </div>

                <PageBlock
                    no-margin
                    content-visible
                    class="wardrobe__content">

                    <div v-sticky="!isDeviceDesktop" class="wardrobe__detail-sticky-tabs">
                        <BaseTabs
                            class="wardrobe__detail-tabs"
                            scrollNavHorizontal
                            onlyClick
                            ref="tabs"
                            @change="tabChange"
                            :forceActiveTabHash="'#'+shelfId">
                            <BaseTab v-for="tab in tabs"
                                :key="tab.hash"
                                :id="'' + tab.hash"
                                :name="tab.name"
                                :link="tab.link" />
                        </BaseTabs>
                    </div>

                    <WardrobeShelf
                        class="shelf_detail wardrobe__detail-shelf"
                        :actions-enable="isCurrentProfile"
                        :shelfId="shelfId"
                        :title="''"
                        :total="shelf.perfumes.total"
                        :default-code="defaultCode"
                        :is-default="!!defaultCode"
                        :is-main="isMain"
                        :is-hidden="isHidden"
                        :is-grid="true"
                        :per-page="50"
                        :perfumes="perfumes"/>
                </PageBlock>
                
            </template>

        </ProfileWrapper>
    </div>
</template>

<script>
import get from 'lodash/get'
import { mapGetters } from 'vuex'
import ClickOutside from 'vue-click-outside'

import wardrobeMixin from '@/mixins/profile/wardrobe'

import Shelf from '@/models/users/accounts/Shelf'
import Account from '@/models/users/accounts/Account'
import User from '@/models/users/User'

import PageBlock from '@/components/layout/AppPageBlock'
import Breadcrumb from '@/components/layout/nav/Breadcrumb'
import ProfileWrapper from '@/components/profile/ProfileWrapper'
import WardrobeShelf from '@/components/wardrobe/WardrobeShelf'
import Actions from '@/components/modal/ui/Actions'
import ButtonSquare from '@/components/ui/buttons/ButtonSquare'

export default {
    name: 'WardrobeDetail',
    layout: 'LayoutProfile',
    directives: { ClickOutside },
    components: {
        PageBlock,
        Breadcrumb,
        ProfileWrapper,
        WardrobeShelf,
        Actions,
        ButtonSquare
    },
    mixins: [wardrobeMixin],
    asyncData({app, query, params, $auth, error}) {
        const baseUrl = app.i18n.__baseUrl
        let userModel = new User({ id: params.userId })
        let currentShelfPerfumesUrl = `/users/${params.userId}/accounts/perfume-shelves/${params.shelfId}/perfumes`

        if(app.$auth.loggedIn && parseInt(get(app.$auth, 'user.id')) === parseInt(params.userId)) {
            userModel = new User()
            currentShelfPerfumesUrl = `/users/accounts/perfume-shelves/${params.shelfId}/perfumes`
        }

        const shelfModel = (new Shelf().params({'per_page': 100, with_perfumes: 'n'})).for(userModel, new Account())

        const requests = [
            userModel.profile().get(),
            shelfModel.get(),
            app.$api.get(currentShelfPerfumesUrl, {params: {per_page: 50}})
        ]

        return Promise.all(requests).then((results) => {
            const profile = get(results, '[0][0]')
            const shelves = get(results, '[1][0].items', [])
            const perfumes = get(results, '[2].data.items', [])
            const shelf = shelves.find(item => parseInt(item.id) === parseInt(params.shelfId))

            const isMyWardrobe = app.$auth.loggedIn &&
                parseInt(app.$auth.user.id) === parseInt(params.userId)

            const profileHref = app.router.resolve({
                name: 'users.detail',
                userId: params.userId
            }).href

            const breadcrumbs = [
                {
                    path: baseUrl,
                    name: app.i18n.t('breadcrumbs.home_page'),
                },
                {
                    path: baseUrl + profileHref,
                    name: get(profile, 'fullName', ''),
                },
                {
                    name: isMyWardrobe ? app.i18n.t('breadcrumbs.wardrobe') : app.i18n.t('breadcrumbs.stranger_wardrobe'),
                    path: baseUrl + profileHref + 'wardrobe',
                },
                {
                    name: get(shelf, 'name', null)
                }
            ];

            const tabs = shelves.map(item => {
                return {
                    hash: item.id,
                    link: app.router.resolve({
                        name: 'WardrobeDetail',
                        params: {
                            userId: profile.id,
                            shelfId: item.id
                        }
                    }).href,
                    name: item.name
                }
            })

            return {
                breadcrumbs,
                profile,
                shelves,
                shelf,
                shelfId: shelf.id,
                isMain: shelf.isMain,
                title: shelf.name,
                isDefault: !!shelf.default,
                isHidden: shelf.isHidden,
                defaultCode: shelf.default,
                perfumes,
                userFullname: get(profile, 'fullName'),
                total: get(shelf, 'perfumes.total', 0),
                tabs
            };
        }).catch((e) => {
            error({statusCode: e.response.status, message: e.response.data});
        });
    },
    data: () => ({
        breadcrumbs: [],
        profile: {},
        perfumes: [],
        shelves: [],
        shelf: {},
        title: null,
        shelfId: null,
        isMain: false,
        isDefault: false,
        isHidden: false,
        prevRoute: null,
        showActions: false,
        defaultCode: null,
        isDetail: true,
        userFullname: null,
        total: null,
        tabs: []
    }),
    globalBus: {
        async 'add-perfume'(data) {
            await this.addPerfume(data)
            this.$nuxt.refresh()
        },
        'update-shelf-items'(){
            this.$nuxt.refresh()
        },
        async 'set-shelf-main'(data) {
            const params = {
                is_main: 'y'
            }

            await this.requestUpdateShelf(data.id, params).then(() => {
                this.isMain = true
            })

            this.$modal.hide()
        },
        async 'rename-shelf'(data) {
            try {
                const params = {
                    name: data.name
                }
                const result = await this.requestUpdateShelf(data.id, params);

                if(result) {
                    this.title = data.name
                    this.$bus.$emit('update-tab-name', {
                        tabId: this.shelfId,
                        name: data.name,
                        moveLine: true
                    })
                    this.$alert.success(this.$t('shelf_name_updated'));
                }
            } catch (e) {
                this.$alert.error(this.$t('alerts.error'));
            }
        },
        async 'hide-shelf'(data) {
            try {
                const params = {
                    is_hidden: 'y'
                }
                await this.requestUpdateShelf(data.id, params);
                this.isHidden = true
                this.$alert.success(this.$t('hide_shelf_text'));
            } catch (e) {
                this.$alert.error(this.$t('alerts.error'));
            }
        },
        async 'set_visible_shelf'(data) {
            try {
                const params = {
                    is_hidden: 'n'
                }
                await this.requestUpdateShelf(data.id, params);
                this.isHidden = false
                this.$alert.success(this.$t('set_shelf_visible_text'));
            } catch (e) {
                this.$alert.error(this.$t('alerts.error'));
            }
        },
        async 'on-action-accept-delete'(shelfId) {
            // удаление полки
            try {
                const userModel = new User()
                await (new Shelf({id: shelfId})).for(userModel, new Account()).delete();
                this.$modal.hide();
                this.$router.push({name: 'Wardrobe', params: {userId: this.profile.id}})
            } catch (e) {
                this.$alert.error(this.$t('alerts.error'));
            }
        },
        'on-action-reject-delete'() {
            // отмена удаления полки
            this.$modal.hide();
        },
    },
    computed: {
        ...mapGetters(['isDeviceDesktop']),
        computedMetaTitle(){
            return this.isCurrentProfile ? 
                this.$options.filters.metaSitenameTitle(this.$t('title', {shelf_name: get(this.shelf, 'name')})) : 
                this.$options.filters.metaSitenameTitle(get(this.profile, 'fullName') + ' - ' + this.$t('title_stranger', {shelf_name: get(this.shelf, 'name')}))
        }
    },
    mounted(){
        this.$bus.$emit('shelf-tab-change', '#' + this.shelfId)
    },
    methods: {
        isTabSelected(id){
            return this.shelfId === id
        },
        tabChange(hash){
            const tab = this.tabs.find(item => '' + item.hash === hash)

            if(tab && tab.hash !== this.shelfId)
                this.$router.push(tab.link)
        },
        hideActions() {
            this.showActions = false;
        },
        onMoreClick() {
            if(this.isDeviceDesktop) {
                this.showActions = true;
                return;
            }

            const modalItems = this.actionModelItems;

            this.showPopup('actions', {
                modalItems
            });
        },
        async requestUpdateShelf(id, params) {
            try {
                const shelf = new Shelf({id});
                const patchedShelf = shelf.for(new User(), new Account());
                await patchedShelf.patchShelf(params);

                this.$modal.hide();
            } catch (e) {
                if(e.response.data.code === 'changing-name-of-standard-shelf-is-forbidden') {
                    this.$alert.error(this.$t('shelf_rename_error'))

                    this.$modal.hide();
                    return
                }
                if(e.response.data.code === 'shelf-already-exists') {
                    this.$alert.error(this.$t('shelf_already_exists'))

                    return
                }
            }
            return true;
        }
    },
    head() {
        return {
            title: this.computedMetaTitle,
            htmlAttrs: {
                class: this.isDeviceDesktop ?
                    'html view_desktop' : 'html view_mobile view_no-footer',
            }
        }
    }
}
</script>

<style lang="less">
.page-block__counter{
    color: @color-gray-light;
}
@import "./style.less";
</style>